/**
 * AdminKraaft — Kraaft (developers.kraaft.co) integration console.
 *
 * TWO connect paths (Kraaft docs recommend picking one per scenario):
 *   • Connect with Kraaft — OAuth 2.0 + PKCE, the one-tap flow (requires
 *     KRAAFT_OAUTH_CLIENT_ID env). Redirect URI is shown so the workspace
 *     owner can register it in their Kraaft OAuth app.
 *   • Paste API key — bearer token `kft_...` created in the Kraaft mobile
 *     app → Settings → Agents → Generate credential. Single-workspace,
 *     no OAuth setup needed. Works today.
 *
 * After OAuth callback, Kraaft redirects here with ?kraaft=connected|error
 * so we can surface success/failure without polling.
 */
import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowsClockwise, PlugsConnected, Plugs, CheckCircle, Warning as WarnIcon,
  Camera as CameraIcon, Link as LinkIcon, Copy, Key, ArrowRight,
} from "@phosphor-icons/react";

export default function AdminKraaft() {
  const [status, setStatus] = useState(null);
  const [oauthCfg, setOauthCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("oauth"); // oauth | apikey

  const loadStatus = async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([
        api.get("/kraaft/status"),
        api.get("/kraaft/oauth/config"),
      ]);
      setStatus(s.data); setOauthCfg(o.data);
      if (s.data?.connected) await loadRecords("all");
    } catch (_) {}
    setLoading(false);
  };

  const loadRecords = async (mode = filter) => {
    try {
      const params = {};
      if (mode === "matched") params.matched = true;
      if (mode === "unmatched") params.matched = false;
      const r = await api.get("/kraaft/records", { params: { ...params, limit: 100 } });
      setRecords(r.data?.records || []); setFilter(mode);
    } catch (_) {}
  };

  // Surface OAuth callback outcome via query-string
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const k = q.get("kraaft");
    if (!k) return;
    const detail = q.get("detail");
    if (k === "connected") toast.success("Kraaft connected!" + (detail ? ` (${detail})` : ""));
    else if (k === "pick-workspace") toast.info("Connected — pick your Kraaft workspace to start the sync");
    else toast.error(`Kraaft connect failed: ${detail || k}`);
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => { loadStatus(); }, []);

  const syncNow = async (mode) => {
    setBusy(true);
    try {
      const r = await api.post(`/kraaft/sync?mode=${mode}`);
      toast.success(`${mode === "backfill" ? "Backfill" : "Poll"}: ${r.data?.mirrored || 0} mirrored · ${r.data?.matched || 0} matched · ${r.data?.events || 0} events`);
      await loadRecords(filter); await loadStatus();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Sync failed")); }
    setBusy(false);
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Kraaft? Mirrored records stay in the DB but polling stops.")) return;
    setBusy(true);
    try {
      await api.post("/kraaft/disconnect");
      toast.success("Kraaft disconnected");
      setStatus(null); setRecords([]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Disconnect failed")); }
    setBusy(false);
  };

  return (
    <AdminShell>
      <div className="p-4 max-w-6xl mx-auto space-y-6" data-testid="kraaft-page">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-md bg-[#0f172a] text-white grid place-content-center font-black">K</div>
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight">Kraaft Bridge</h1>
            <div className="text-sm text-slate-600">Mirror photos and notes from your Kraaft workspace onto Field Radar Work Orders.</div>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : !status?.connected ? (
          <>
            <EmailBridgePanel/>
            <ConnectSection
              tab={tab} setTab={setTab}
              oauthCfg={oauthCfg}
              onConnected={loadStatus}
              busy={busy} setBusy={setBusy}
            />
          </>
        ) : status?.needs_workspace ? (
          <PickWorkspacePanel onDone={loadStatus} busy={busy} setBusy={setBusy}/>
        ) : (
          <>
            <EmailBridgePanel/>
            <StatusCard status={status} onSync={syncNow} onDisconnect={disconnect} busy={busy}/>
            <RecordsPanel records={records} filter={filter} onFilter={loadRecords}/>
          </>
        )}
      </div>
    </AdminShell>
  );
}


function EmailBridgePanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [scope, setScope] = useState("assigned");
  const [enabled, setEnabled] = useState(true);
  const [includeContact, setIncludeContact] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/kraaft/push/config");
      setCfg(r.data);
      setEmail(r.data.push_email || "");
      setSenderEmail(r.data.sender_email || "");
      setScope(r.data.scope || "assigned");
      setEnabled(!!r.data.enabled);
      setIncludeContact(r.data.include_tenant_contact !== false);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/kraaft/push/config", {
        push_email: email.trim(),
        enabled, scope,
        include_tenant_contact: includeContact,
        sender_email: senderEmail.trim() || null,
      });
      toast.success("Email Bridge saved");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post("/kraaft/push/test", { push_email: email.trim() || undefined });
      if (r.data?.sent) toast.success(`Test sent to ${r.data.to} — check your Kraaft chat`);
      else toast.warning("Send attempted but returned false — check email killswitch or logs");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Test failed")); }
    setBusy(false);
  };

  if (!cfg) return null;

  return (
    <div className="rounded-lg border-2 border-fuchsia-200 bg-fuchsia-50/40 p-6 shadow-sm" data-testid="kraaft-email-bridge">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-xs font-black uppercase tracking-widest text-fuchsia-700">→ Field Radar to Kraaft</div>
          <h2 className="font-display font-black text-xl">Email Bridge</h2>
          <p className="text-xs text-slate-600 mt-0.5">Every new WO auto-posts into your Kraaft crew chat as a conversation — with tenant contact + address + assignment.</p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} data-testid="kraaft-push-enabled"/>
          <span className="text-xs font-black uppercase tracking-widest">{enabled ? "ON" : "OFF"}</span>
        </label>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-black uppercase tracking-widest text-slate-600">Kraaft inbound email <span className="text-slate-400 font-normal">(forwarding destination)</span></label>
          <input
            type="email" data-testid="kraaft-push-email-input"
            value={email} onChange={e => setEmail(e.target.value)}
            placeholder="xxxx@inbound.kraaft.co"
            className="mt-1 w-full h-11 px-3 border-2 border-slate-300 focus:border-fuchsia-500 font-mono text-sm outline-none bg-white"
          />
          <div className="mt-1 text-[10px] text-slate-500">
            From Kraaft: Settings → Integrations → Email → Activate a kraaft email → copy the whole address.
          </div>
        </div>

        <div>
          <label className="text-xs font-black uppercase tracking-widest text-slate-600">Your Kraaft-member inbox <span className="text-fuchsia-600">(where Field Radar mails you)</span></label>
          <input
            type="email" data-testid="kraaft-push-sender-input"
            value={senderEmail} onChange={e => setSenderEmail(e.target.value)}
            placeholder="andrew@goprocall.com"
            className="mt-1 w-full h-11 px-3 border-2 border-slate-300 focus:border-fuchsia-500 font-mono text-sm outline-none bg-white"
          />
          <div className="mt-1 text-[10px] text-slate-500">
            Field Radar sends the WO email <b>here</b> (not directly to Kraaft) because Kraaft's inbound whitelist rejects our mail proxy. You then use a <b>one-time Gmail filter</b> (steps below) to auto-forward to the Kraaft address above.
          </div>
        </div>

        <details className="rounded border-2 border-fuchsia-300 bg-white p-3">
          <summary className="cursor-pointer text-xs font-black uppercase tracking-widest text-fuchsia-700">Gmail auto-forward setup (one time, 90 seconds)</summary>
          <ol className="mt-3 text-xs text-slate-700 list-decimal ml-5 space-y-1.5">
            <li>Open <b>Gmail → Settings ⚙️ → See all settings → Forwarding and POP/IMAP</b></li>
            <li>Click <b>Add a forwarding address</b> → paste <code className="font-mono text-[11px]">{email || "72isoplickzo@inbound.kraaft.co"}</code> → click Next → OK</li>
            <li>Kraaft will send a confirmation code back to your Gmail — open it, copy the code, paste it into Gmail to verify</li>
            <li>Go to <b>Filters and Blocked Addresses → Create a new filter</b></li>
            <li>In the <b>Subject</b> field, paste exactly: <code className="font-mono text-[11px] bg-fuchsia-50 px-1">[FR→Kraaft]</code></li>
            <li>Click <b>Create filter</b> → check <b>Forward it to:</b> and pick <code className="font-mono text-[11px]">{email || "72isoplickzo@inbound.kraaft.co"}</code></li>
            <li>Also check <b>Skip the Inbox</b> if you don't want the copies cluttering your Gmail</li>
            <li>Click <b>Create filter</b> — done. Every WO from now on flies through automatically.</li>
          </ol>
        </details>

        <div>
          <label className="text-xs font-black uppercase tracking-widest text-slate-600">Which WOs to push</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {[
              { v: "assigned",  l: "Assigned WOs",  hint: "Only when a tech is on it (recommended)" },
              { v: "all",       l: "Every WO",      hint: "Noisier but complete" },
              { v: "emergency", l: "Habitability & emergency only", hint: "Signal-only" },
            ].map(o => (
              <button key={o.v} type="button" onClick={() => setScope(o.v)}
                data-testid={`kraaft-push-scope-${o.v}`}
                className={`text-left px-3 py-2 border-2 text-xs font-semibold ${scope === o.v ? "border-fuchsia-600 bg-fuchsia-100 text-fuchsia-900" : "border-slate-300 bg-white text-slate-700"}`}>
                <div className="font-black uppercase tracking-wider text-[10px]">{o.l}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{o.hint}</div>
              </button>
            ))}
          </div>
        </div>

        <label className="inline-flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={includeContact} onChange={e => setIncludeContact(e.target.checked)} data-testid="kraaft-push-contact"/>
          <span className="text-xs text-slate-700">Include tenant name + phone (crew can tap-to-call from Kraaft)</span>
        </label>

        <div className="flex items-center gap-2 flex-wrap pt-1">
          <button type="button" onClick={save} disabled={busy || !email}
            data-testid="kraaft-push-save-btn"
            className="h-10 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs disabled:opacity-50">
            Save Email Bridge
          </button>
          <button type="button" onClick={test} disabled={busy || !email}
            data-testid="kraaft-push-test-btn"
            className="h-10 px-4 bg-white border-2 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 font-black uppercase tracking-widest text-xs disabled:opacity-50">
            Send test message
          </button>
          {cfg.last_push_at && (
            <span className="text-[10px] text-slate-500">
              Last push: {new Date(cfg.last_push_at).toLocaleString()} · {cfg.last_push_count || 0} total
            </span>
          )}
        </div>
        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-2 space-y-1">
          <div><b>Why the Gmail hop?</b> Emergent's mail proxy hard-locks the sender address (Kraaft dropped our direct sends silently). Bouncing through your Gmail makes the forward come from you, a workspace member Kraaft trusts. One-time filter, works forever after.</div>
          <div className="pt-1 border-t border-amber-200"><b>Test-mode note:</b> Field Radar's TEST MODE (red banner) blocks all outbound emails EXCEPT this bridge, which has a surgical bypass. So the test button below will send even in test mode.</div>
        </div>
        {cfg.last_push_error && (
          <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            <b>Last error:</b> {cfg.last_push_error}
          </div>
        )}
      </div>
    </div>
  );
}


function ConnectSection({ tab, setTab, oauthCfg, onConnected, busy, setBusy }) {
  return (
    <div className="rounded-lg border-2 border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Plugs weight="bold" size={20} className="text-slate-400"/>
        <div className="text-xs font-black uppercase tracking-widest text-slate-500">Not connected</div>
      </div>
      <h2 className="font-display font-black text-xl">Connect your Kraaft workspace</h2>

      <div className="mt-4 flex items-center gap-1 border-b border-slate-200">
        <TabBtn active={tab === "oauth"} onClick={() => setTab("oauth")} testid="kraaft-tab-oauth">
          <PlugsConnected weight="bold" size={14}/> Connect with Kraaft
          <span className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-black">RECOMMENDED</span>
        </TabBtn>
        <TabBtn active={tab === "apikey"} onClick={() => setTab("apikey")} testid="kraaft-tab-apikey">
          <Key weight="bold" size={14}/> Paste API key
        </TabBtn>
      </div>

      {tab === "oauth" ? (
        <OAuthPanel oauthCfg={oauthCfg} busy={busy} setBusy={setBusy}/>
      ) : (
        <ApiKeyPanel onConnected={onConnected} busy={busy} setBusy={setBusy}/>
      )}
    </div>
  );
}


function TabBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 h-10 px-4 text-xs font-black uppercase tracking-widest border-b-2 -mb-[2px] ${active ? "border-[#0f172a] text-[#0f172a]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
      {children}
    </button>
  );
}


function OAuthPanel({ oauthCfg, busy, setBusy }) {
  const [copied, setCopied] = useState(false);
  const redirect = oauthCfg?.redirect_uri || "";

  const startConnect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/kraaft/oauth/start");
      if (r.data?.authorize_url) window.location.href = r.data.authorize_url;
      else toast.error("No authorize URL returned");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not start OAuth")); }
    setBusy(false);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(redirect); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch (_) {}
  };

  return (
    <div className="mt-5 space-y-4">
      <p className="text-sm text-slate-600">
        The one-tap flow. Click below → sign in on <span className="font-mono">auth.kraaft.co</span> → approve → land back here with a live sync.
      </p>

      {oauthCfg?.enabled ? (
        <button type="button" onClick={startConnect} disabled={busy}
          data-testid="kraaft-oauth-start-btn"
          className="w-full h-14 bg-[#1a73e8] hover:bg-[#1666d1] text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2 rounded shadow">
          <PlugsConnected weight="fill" size={18}/> Connect with Kraaft <ArrowRight weight="bold" size={16}/>
        </button>
      ) : (
        <div className="p-4 border-2 border-amber-300 bg-amber-50 rounded space-y-3">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-amber-800">
            <WarnIcon weight="fill" size={14}/> OAuth app not configured yet
          </div>
          <div className="text-sm text-slate-700 space-y-2">
            <p>
              Kraaft's OAuth panel is <b>owner-only</b> and lives inside their web app.
              Once you've made an OAuth app for Field Radar, drop the Client ID (and
              Secret, if it's a confidential client) into <span className="font-mono text-xs">/app/backend/.env</span> as{" "}
              <span className="font-mono text-xs">KRAAFT_OAUTH_CLIENT_ID</span> and{" "}
              <span className="font-mono text-xs">KRAAFT_OAUTH_CLIENT_SECRET</span> and reload.
            </p>
            <p className="text-xs text-slate-600 italic">
              👉 Can't find the OAuth panel in your Kraaft mobile app? Use the <b>Paste API key</b>
              tab for now — you'll be up and running in under a minute.
            </p>
          </div>
        </div>
      )}

      <div className="rounded border-2 border-dashed border-slate-300 bg-slate-50 p-3">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex items-center gap-1 mb-1">
          <LinkIcon weight="bold" size={11}/> Redirect URI (register this at developers.kraaft.co)
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 text-[11px] font-mono text-slate-800 bg-white px-2 py-1.5 rounded border border-slate-200 truncate" data-testid="kraaft-redirect-uri">
            {redirect || "(no BACKEND_PUBLIC_URL set)"}
          </code>
          <button type="button" onClick={copy} disabled={!redirect}
            data-testid="kraaft-copy-redirect-btn"
            className="h-8 px-2 text-[10px] font-black uppercase tracking-widest bg-white border-2 border-slate-300 hover:border-slate-400 disabled:opacity-50 inline-flex items-center gap-1">
            <Copy weight="bold" size={11}/> {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="mt-2 text-[10px] text-slate-500">
          Scopes requested: <span className="font-mono">{(oauthCfg?.scopes || []).join(" · ")}</span>
        </div>
      </div>
    </div>
  );
}


function ApiKeyPanel({ onConnected, busy, setBusy }) {
  const [token, setToken] = useState("");
  const [workspaces, setWorkspaces] = useState(null);
  const [chosenWs, setChosenWs] = useState("");
  const [chosenSchema, setChosenSchema] = useState("");
  const [schemas, setSchemas] = useState([]);

  const test = async () => {
    if (!token.trim()) { toast.error("Paste a Kraaft API key first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/kraaft/test-connection", { api_token: token.trim() });
      setWorkspaces(r.data?.workspaces || []);
      if ((r.data?.workspaces || []).length === 1) setChosenWs(r.data.workspaces[0].id || "");
      toast.success(`Key OK — ${r.data?.workspaces?.length || 0} workspace(s) available`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Kraaft rejected the key")); }
    setBusy(false);
  };

  const connect = async () => {
    if (!chosenWs) { toast.error("Pick a workspace"); return; }
    setBusy(true);
    try {
      await api.post("/kraaft/connect", {
        api_token: token.trim(),
        workspace_id: chosenWs,
        wo_schema_id: chosenSchema || null,
      });
      toast.success("Kraaft connected — pulling first records now");
      try {
        const r = await api.post("/kraaft/sync?mode=backfill");
        toast.success(`Backfill: ${r.data?.mirrored || 0} record(s), ${r.data?.matched || 0} matched to WOs`);
      } catch (_) {}
      await onConnected();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Connect failed")); }
    setBusy(false);
  };

  return (
    <div className="mt-5 space-y-4">
      <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-1">
        <div className="font-black uppercase tracking-widest text-slate-500 text-[10px]">Where to find your Kraaft key</div>
        <ol className="list-decimal ml-5 space-y-0.5">
          <li>Open the <b>Kraaft app</b> (mobile or web)</li>
          <li>Tap your workspace name → <b>Settings</b> (⚙️)</li>
          <li>Scroll to <b>Agents</b> → <b>Generate credential</b></li>
          <li>Copy the whole key — starts with <code className="font-mono">kft_</code> — and paste it below</li>
        </ol>
        <div className="text-[10px] text-slate-500 italic pt-1">Only workspace owners can create keys. If you don't see "Agents" you may need to be promoted.</div>
      </div>

      <div>
        <label className="text-xs font-black uppercase tracking-widest text-slate-600">Kraaft API key</label>
        <input
          type="password"
          data-testid="kraaft-token-input"
          value={token}
          onChange={e => setToken(e.target.value)}
          placeholder="kft_..."
          className="mt-1 w-full h-11 px-3 border-2 border-slate-300 focus:border-[#00C4CC] font-mono text-sm outline-none"
        />
      </div>
      <button
        type="button"
        data-testid="kraaft-test-btn"
        disabled={busy || !token.trim()}
        onClick={test}
        className="h-10 px-4 bg-slate-900 text-white font-black uppercase tracking-widest text-xs disabled:opacity-50 inline-flex items-center gap-2"
      >
        <PlugsConnected weight="bold" size={14}/> Test key
      </button>

      {workspaces && (
        <div className="mt-2 space-y-3 p-4 border-2 border-emerald-200 bg-emerald-50/50 rounded">
          <div className="text-xs font-black uppercase tracking-widest text-emerald-700 inline-flex items-center gap-1">
            <CheckCircle weight="fill" size={14}/> Key OK
          </div>
          <div>
            <label className="text-xs font-black uppercase tracking-widest text-slate-600">Workspace</label>
            <select
              data-testid="kraaft-workspace-select"
              value={chosenWs} onChange={e => setChosenWs(e.target.value)}
              className="mt-1 w-full h-11 px-3 border-2 border-slate-300 bg-white font-medium text-sm outline-none"
            >
              <option value="">— pick a workspace —</option>
              {workspaces.map(w => <option key={w.id} value={w.id}>{w.name || w.id}</option>)}
            </select>
          </div>

          <button
            type="button"
            data-testid="kraaft-connect-btn"
            disabled={busy || !chosenWs}
            onClick={connect}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <PlugsConnected weight="fill" size={16}/> Connect & run first sync
          </button>
        </div>
      )}
    </div>
  );
}


function PickWorkspacePanel({ onDone, busy, setBusy }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [chosen, setChosen] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/kraaft/oauth/workspaces");
        const ws = r.data?.workspaces || [];
        setWorkspaces(ws);
        if (ws.length === 1) setChosen(ws[0].id);
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not list workspaces")); }
    })();
  }, []);
  const save = async () => {
    if (!chosen) { toast.error("Pick a workspace"); return; }
    setBusy(true);
    try {
      await api.post("/kraaft/oauth/pick-workspace", { workspace_id: chosen });
      try { await api.post("/kraaft/sync?mode=backfill"); } catch (_) {}
      toast.success("Workspace selected — mirror running");
      await onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-6 shadow-sm" data-testid="kraaft-pick-ws-panel">
      <div className="text-xs font-black uppercase tracking-widest text-emerald-700 inline-flex items-center gap-1">
        <CheckCircle weight="fill" size={14}/> OAuth connected
      </div>
      <h2 className="mt-2 font-display font-black text-xl">Choose your workspace</h2>
      <p className="mt-1 text-sm text-slate-600">Field Radar has access to {workspaces.length} workspace(s). Pick the one whose photos + notes should mirror onto your WOs.</p>
      <select value={chosen} onChange={e => setChosen(e.target.value)}
        data-testid="kraaft-oauth-ws-select"
        className="mt-3 w-full h-11 px-3 border-2 border-slate-300 bg-white font-medium text-sm outline-none">
        <option value="">— pick a workspace —</option>
        {workspaces.map(w => <option key={w.id} value={w.id}>{w.name || w.id}</option>)}
      </select>
      <button type="button" onClick={save} disabled={busy || !chosen}
        data-testid="kraaft-oauth-save-ws-btn"
        className="mt-4 w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50">
        Save & start mirror
      </button>
    </div>
  );
}


function StatusCard({ status, onSync, onDisconnect, busy }) {
  const state = status.status || "connected";
  const stateColor = state === "connected" ? "text-emerald-700 bg-emerald-100" : state === "error" ? "text-red-700 bg-red-100" : "text-amber-700 bg-amber-100";
  return (
    <div className="rounded-lg border-2 border-slate-200 bg-white p-6 shadow-sm" data-testid="kraaft-status-card">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded ${stateColor}`}>
            {state === "connected" ? <CheckCircle weight="fill" size={12}/> : <WarnIcon weight="fill" size={12}/>}
            {state}
          </div>
          <span className="ml-2 text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
            {status.auth_kind === "oauth" ? "OAuth 2.0" : "API Key"}
          </span>
          <h2 className="mt-2 font-display font-black text-xl">Kraaft is connected</h2>
          <div className="mt-1 text-xs text-slate-600">
            Workspace <span className="font-mono">{status.workspace_id}</span>
            {status.wo_schema_id && <> · Schema <span className="font-mono">{status.wo_schema_id}</span></>}
            {status.token_preview && <> · Token <span className="font-mono">{status.token_preview}</span></>}
          </div>
          {status.last_sync_at && (
            <div className="mt-2 text-xs text-slate-500">
              Last sync <b>{new Date(status.last_sync_at).toLocaleString()}</b> · {status.last_sync_mode} · {status.last_sync_events || 0} events · {status.last_sync_mirrored || 0} records · {status.last_sync_matched || 0} matched to WOs
            </div>
          )}
          {status.last_sync_error && (
            <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              <b>Last error:</b> {status.last_sync_error}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => onSync("poll")} disabled={busy}
            data-testid="kraaft-poll-btn"
            className="h-10 px-3 bg-[#0f172a] text-white font-black uppercase tracking-widest text-[11px] disabled:opacity-50 inline-flex items-center gap-1">
            <ArrowsClockwise weight="bold" size={14}/> Poll now
          </button>
          <button type="button" onClick={() => onSync("backfill")} disabled={busy}
            data-testid="kraaft-backfill-btn"
            className="h-10 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-[11px] disabled:opacity-50 inline-flex items-center gap-1">
            <ArrowsClockwise weight="fill" size={14}/> Full backfill
          </button>
          <button type="button" onClick={onDisconnect} disabled={busy}
            data-testid="kraaft-disconnect-btn"
            className="h-10 px-3 bg-white border-2 border-slate-300 text-slate-700 font-black uppercase tracking-widest text-[11px] disabled:opacity-50">
            Disconnect
          </button>
        </div>
      </div>
    </div>
  );
}


function RecordsPanel({ records, filter, onFilter }) {
  return (
    <div className="rounded-lg border-2 border-slate-200 bg-white p-6 shadow-sm" data-testid="kraaft-records-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display font-black text-lg">Mirrored records ({records.length})</h2>
        <div className="flex items-center gap-1">
          {["all", "matched", "unmatched"].map(k => (
            <button key={k} type="button" onClick={() => onFilter(k)}
              data-testid={`kraaft-filter-${k}`}
              className={`h-8 px-3 text-[10px] font-black uppercase tracking-widest ${filter === k ? "bg-[#0f172a] text-white" : "bg-white border border-slate-300 text-slate-700"}`}>
              {k}
            </button>
          ))}
        </div>
      </div>
      {records.length === 0 ? (
        <div className="text-sm text-slate-500 italic">Nothing here yet. Kick off a Full Backfill (top-right) or wait for the 5-minute poll cron.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {records.map(r => <KraaftRecordCard key={r._id} rec={r}/>)}
        </div>
      )}
    </div>
  );
}


export function KraaftRecordCard({ rec }) {
  const photos = rec.photos || [];
  return (
    <div className="rounded-md border-2 border-slate-200 bg-white p-3 space-y-2" data-testid={`kraaft-record-${rec.record_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate">{rec.title || rec.address || "Kraaft record"}</div>
          <div className="text-[11px] text-slate-500 truncate">
            {rec.address}{rec.unit ? ` · Unit ${rec.unit}` : ""}
            {rec.wo_number && <> · WO#{rec.wo_number}</>}
          </div>
        </div>
        {rec.matched_job_id ? (
          <a href={`/admin/jobs/${rec.matched_job_id}`} className="shrink-0 text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
            <LinkIcon weight="bold" size={10}/> Matched
          </a>
        ) : (
          <span className="shrink-0 text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-100 px-2 py-0.5 rounded">Unmatched</span>
        )}
      </div>
      {rec.notes && <div className="text-xs text-slate-700 whitespace-pre-wrap line-clamp-3">{rec.notes}</div>}
      {photos.length > 0 && (
        <div className="flex gap-1 flex-wrap">
          {photos.slice(0, 6).map((p, i) => (
            <a key={i} href={p.url} target="_blank" rel="noreferrer" className="block h-14 w-14 border border-slate-200 overflow-hidden">
              <img src={p.url} alt={p.name || `photo ${i}`} className="h-full w-full object-cover"/>
            </a>
          ))}
          {photos.length > 6 && <span className="text-[10px] font-bold text-slate-500 self-center">+{photos.length - 6}</span>}
        </div>
      )}
      <div className="text-[10px] text-slate-400 flex items-center gap-1">
        <CameraIcon weight="bold" size={10}/> {photos.length} photo{photos.length === 1 ? "" : "s"} · Mirrored {rec.mirrored_at ? new Date(rec.mirrored_at).toLocaleString() : ""}
      </div>
    </div>
  );
}
